import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, CurrencyInr, ClockCounterClockwise, CheckCircle, Warning, TrendUp, UserPlus, FilePlus, Receipt, UploadSimple, Megaphone, ChartBar, Robot, IdentificationBadge, ShoppingCart, Sparkle, HouseSimple, FileText, BookOpenText, Folder, Printer, Image as ImageIcon, FilePdf, Camera, Gear, Buildings, ArrowRight, MapPin } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import GlobalSearch from "@/components/GlobalSearch";
import { useAuth } from "@/context/AuthContext";

const STAT_COLORS = ["#EA580C", "#1E3A8A", "#16A34A", "#F59E0B", "#DC2626", "#7C3AED"];

function Stat({ label, value, icon: Icon, tone = "orange", testid }) {
  const toneMap = {
    orange: "bg-orange-50 text-orange-600 border-orange-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-600 border-red-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Card className="p-5 border border-slate-200 shadow-sm rounded-lg bg-white h-full" data-testid={testid}>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center border ${toneMap[tone]} mb-3`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
      </Card>
    </motion.div>
  );
}

const QUICK_ACTIONS = [
  { icon: UserPlus,    label: "New Customer",     to: "/customers",          tone: "text-orange-600  bg-orange-50  ring-orange-200",  testid: "qa-new-customer" },
  { icon: FilePlus,    label: "New Application",  to: "/applications",       tone: "text-slate-800   bg-slate-100  ring-slate-200",   testid: "qa-new-app" },
  { icon: Receipt,     label: "New Bill",         to: "/applications?new=1", tone: "text-emerald-700 bg-emerald-50 ring-emerald-200", testid: "qa-new-bill" },
  { icon: ShoppingCart,label: "POS Billing",      to: "/pos",                tone: "text-purple-700  bg-purple-50  ring-purple-200",  testid: "qa-pos" },
  { icon: Robot,       label: "AI Draft",         to: "/ai-drafts",          tone: "text-fuchsia-700 bg-fuchsia-50 ring-fuchsia-200", testid: "qa-ai-draft" },
  { icon: HouseSimple, label: "Rent Agreement",   to: "/rent-agreements",    tone: "text-blue-700    bg-blue-50    ring-blue-200",    testid: "qa-rent" },
  { icon: Folder,      label: "Document Manager", to: "/customers",          tone: "text-cyan-700    bg-cyan-50    ring-cyan-200",    testid: "qa-doc-mgr" },
  { icon: Printer,     label: "Print Center",     to: "/print-center",       tone: "text-indigo-700  bg-indigo-50  ring-indigo-200",  testid: "qa-print-center" },
  { icon: ImageIcon,   label: "AI Photo Studio",  to: "/photo-studio",       tone: "text-pink-700    bg-pink-50    ring-pink-200",    testid: "qa-photo-studio" },
  { icon: FilePdf,     label: "PDF Tools",        to: "/pdf-studio",         tone: "text-red-700     bg-red-50     ring-red-200",     testid: "qa-pdf-tools" },
  { icon: Camera,      label: "Scan Document",    to: "/photo-studio?tab=scan", tone: "text-teal-700    bg-teal-50    ring-teal-200",    testid: "qa-scan-doc" },
  { icon: UploadSimple,label: "Import Excel",     to: "/data-import",        tone: "text-amber-700   bg-amber-50   ring-amber-200",   testid: "qa-import-excel" },
  { icon: ChartBar,    label: "Reports",          to: "/reports",            tone: "text-slate-700   bg-slate-100  ring-slate-200",   testid: "qa-reports" },
  { icon: Megaphone,   label: "Marketing",        to: "/marketing",          tone: "text-rose-700    bg-rose-50    ring-rose-200",    testid: "qa-marketing" },
  { icon: CurrencyInr, label: "Payment Center",   to: "/payment-center",     tone: "text-emerald-700 bg-emerald-50 ring-emerald-200", testid: "qa-payments" },
  { icon: Gear,        label: "Settings",         to: "/branches",           tone: "text-slate-600   bg-slate-100  ring-slate-200",   testid: "qa-settings" },
];

// One-click AI Draft templates shown on the Dashboard AI section
const AI_DRAFT_SHORTCUTS = [
  { icon: HouseSimple, label: "Rent Agreement", tone: "bg-blue-50 text-blue-700 border-blue-200", template: "rent_residential", testid: "ai-shortcut-rent" },
  { icon: FileText, label: "Leave & License", tone: "bg-indigo-50 text-indigo-700 border-indigo-200", template: "rent_residential", testid: "ai-shortcut-ll" },
  { icon: FileText, label: "Electricity Meter NOC", tone: "bg-amber-50 text-amber-700 border-amber-200", template: "noc_electricity_meter", testid: "ai-shortcut-noc-elec" },
  { icon: FileText, label: "NOC · Property", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", template: "noc_property", testid: "ai-shortcut-noc-prop" },
  { icon: FileText, label: "Affidavit · Name Change", tone: "bg-orange-50 text-orange-700 border-orange-200", template: "aff_name_change", testid: "ai-shortcut-aff-name" },
  { icon: FileText, label: "Gazette Name Change", tone: "bg-red-50 text-red-700 border-red-200", template: "gov_gazette_name", testid: "ai-shortcut-gazette" },
  { icon: FileText, label: "PAN Covering Letter", tone: "bg-slate-100 text-slate-700 border-slate-200", template: "gov_pan_letter", testid: "ai-shortcut-pan" },
  { icon: FileText, label: "Passport Letter", tone: "bg-purple-50 text-purple-700 border-purple-200", template: "gov_passport_letter", testid: "ai-shortcut-passport" },
];

const QUICK_FIND = [
  { icon: Users, label: "Find Customer", to: "/customers", accent: "orange" },
  { icon: FilePlus, label: "Find Application", to: "/applications", accent: "blue" },
  { icon: Receipt, label: "Find Bill", to: "/billing", accent: "emerald" },
  { icon: UploadSimple, label: "Find Documents", to: "/customers", accent: "purple" },
  { icon: Warning, label: "Find Complaint", to: "/complaints", accent: "red" },
  { icon: IdentificationBadge, label: "Find Staff", to: "/staff", accent: "amber" },
];

function useRecentSearches() {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("csc_recent_searches") || "[]"); }
    catch { return []; }
  });
  return items;
}

function BillingMetric({ label, value, tone }) {
  const t = {
    blue: "text-blue-700",
    green: "text-emerald-700",
    orange: "text-orange-600",
    amber: "text-amber-700",
  }[tone] || "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${t}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [draftStats, setDraftStats] = useState({ total: 0, today: 0, draft: 0, approved: 0, archived: 0 });
  const [recentDrafts, setRecentDrafts] = useState([]);
  const [branchRows, setBranchRows] = useState([]);
  const nav = useNavigate();
  const recent = useRecentSearches();

  const showBranchWise = user && ["super_admin", "admin", "branch_admin"].includes(user.role);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/ai/drafts/stats").then((r) => setDraftStats(r.data)).catch(() => {});
    api.get("/ai/drafts", { params: { limit: 5 } }).then((r) => setRecentDrafts(r.data)).catch(() => {});
    if (showBranchWise) {
      api.get("/dashboard/branch-summary").then((r) => setBranchRows(r.data)).catch(() => {});
    }
  }, [showBranchWise]);

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Overview</div>
        <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Dashboard</h1>
        <p className="text-sm text-slate-600 mt-1">Search anything · run today's counter · file new work in one tap.</p>
      </div>

      {/* Global Search */}
      <div className="mb-6"><GlobalSearch /></div>

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={12} weight="fill" className="text-orange-500" />
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Quick Actions</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5" data-testid="quick-actions-grid">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.label}
              data-testid={a.testid}
              onClick={() => {
                if (a.comingSoon) { toast.info(`${a.label} is coming soon — under active build.`); return; }
                nav(a.to);
              }}
              className={`group relative flex items-center gap-2.5 px-3 h-14 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 text-left`}
            >
              <div className={`w-9 h-9 rounded-md ring-1 flex items-center justify-center shrink-0 ${a.tone}`}>
                <a.icon size={18} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-900 leading-tight truncate">{a.label}</div>
                {a.comingSoon && <div className="text-[9px] uppercase tracking-widest text-slate-400 mt-0.5">Soon</div>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      {!stats ? (
        <div className="text-slate-500 py-20 text-center" data-testid="dashboard-loading">Loading…</div>
      ) : (
        <>
          {/* Priority row */}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-3">Application Priority Queue</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <button
                onClick={() => nav("/applications")}
                data-testid="priority-total"
                className="text-left p-5 rounded-lg bg-slate-900 text-white border border-slate-900 hover:-translate-y-px hover:shadow-lg transition-[transform,box-shadow] duration-200"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Total Applications</div>
                <div className="text-3xl font-bold mt-2" style={{ fontFamily: "var(--font-heading)" }}>{stats.total_applications || 0}</div>
              </button>
              <button
                onClick={() => nav("/applications?priority=Emergency")}
                data-testid="priority-emergency"
                className="text-left p-5 rounded-lg bg-white border-l-4 border-red-500 border border-slate-200 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow] duration-200"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-600 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />Emergency
                </div>
                <div className="text-3xl font-bold mt-2 text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{stats.priority_emergency || 0}</div>
                <div className="text-xs text-red-600 mt-1">Requires immediate action</div>
              </button>
              <button
                onClick={() => nav("/applications?priority=Urgent")}
                data-testid="priority-urgent"
                className="text-left p-5 rounded-lg bg-white border-l-4 border-amber-500 border border-slate-200 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow] duration-200"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-amber-600 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />Urgent
                </div>
                <div className="text-3xl font-bold mt-2 text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{stats.priority_urgent || 0}</div>
                <div className="text-xs text-amber-700 mt-1">Prioritise today</div>
              </button>
              <button
                onClick={() => nav("/applications?priority=Normal")}
                data-testid="priority-normal"
                className="text-left p-5 rounded-lg bg-white border-l-4 border-emerald-500 border border-slate-200 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow] duration-200"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />Normal
                </div>
                <div className="text-3xl font-bold mt-2 text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{stats.priority_normal || 0}</div>
                <div className="text-xs text-emerald-700 mt-1">Standard processing</div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-8">
            <Stat testid="stat-customers-today" label="Customers Today" value={stats.customers_today} icon={Users} tone="orange" />
            <Stat testid="stat-revenue-today" label="Revenue Today" value={inr(stats.revenue_today)} icon={CurrencyInr} tone="green" />
            <Stat testid="stat-pending-apps" label="Pending Apps" value={stats.pending_applications} icon={ClockCounterClockwise} tone="amber" />
            <Stat testid="stat-completed" label="Completed" value={stats.completed_services} icon={CheckCircle} tone="green" />
            <Stat testid="stat-open-complaints" label="Open Complaints" value={stats.open_complaints} icon={Warning} tone="red" />
            <Stat testid="stat-pending-payments" label="Pending Payments" value={stats.pending_payments || 0} icon={CurrencyInr} tone="amber" />
            <Stat testid="stat-total-customers" label="Total Customers" value={stats.total_customers} icon={TrendUp} tone="blue" />
          </div>

          {/* Billing widget with one-click New Bill */}
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-3 flex items-center gap-2">
              <Receipt size={12} weight="duotone" /> Billing
            </div>
            <Card className="bg-white border-slate-200 p-5" data-testid="dashboard-billing-widget">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <BillingMetric label="Today's Bills" value={stats.bills_today || 0} tone="blue" />
                <BillingMetric label="Today's Collection" value={inr(stats.revenue_today || 0)} tone="green" />
                <BillingMetric label="GST Collected Today" value={inr(stats.gst_today || 0)} tone="orange" />
                <BillingMetric label="Outstanding" value={inr(stats.outstanding_total || 0)} tone="amber" />
              </div>
              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                <button
                  onClick={() => nav("/applications?new=1")}
                  data-testid="dashboard-new-bill"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200"
                >
                  <FilePlus size={16} weight="bold" /> New Bill
                </button>
                <button
                  onClick={() => nav("/pos")}
                  data-testid="dashboard-pos"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200"
                >
                  <ShoppingCart size={16} weight="bold" /> POS Billing
                </button>
                <button
                  onClick={() => nav("/billing")}
                  data-testid="dashboard-view-bills"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200"
                >
                  <Receipt size={16} weight="duotone" /> View All Bills
                </button>
                <button
                  onClick={() => nav("/payments")}
                  data-testid="dashboard-billing-report"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-medium transition-colors"
                >
                  <ChartBar size={16} weight="duotone" /> Billing Report
                </button>
                <button
                  onClick={() => nav("/billing?status=Unpaid")}
                  data-testid="dashboard-outstanding"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-medium transition-colors"
                >
                  <Warning size={16} weight="duotone" /> View Outstanding
                </button>
              </div>
            </Card>
          </div>

          {/* AI Drafts */}
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-3 flex items-center gap-2">
              <Sparkle size={12} weight="duotone" /> AI Drafts
            </div>
            <Card className="bg-white border-slate-200 p-5" data-testid="dashboard-ai-drafts">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <BillingMetric label="Today's Drafts" value={draftStats.today || 0} tone="orange" />
                <BillingMetric label="Draft" value={draftStats.draft || 0} tone="amber" />
                <BillingMetric label="Approved" value={draftStats.approved || 0} tone="green" />
                <BillingMetric label="Archived" value={draftStats.archived || 0} tone="blue" />
                <BillingMetric label="Total Drafts" value={draftStats.total || 0} tone="blue" />
              </div>

              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">One-click templates</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {AI_DRAFT_SHORTCUTS.map((t) => (
                  <button
                    key={t.label}
                    onClick={() => nav(`/ai-drafts?t=generate&template=${t.template}`)}
                    data-testid={t.testid}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all hover:-translate-y-px hover:shadow-sm ${t.tone}`}
                  >
                    <t.icon size={18} weight="duotone" className="shrink-0" />
                    <span className="text-xs font-medium">{t.label}</span>
                  </button>
                ))}
              </div>

              {recentDrafts.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Recent drafts</div>
                  <div className="space-y-1 mb-4">
                    {recentDrafts.slice(0, 5).map((d) => (
                      <button
                        key={d.id}
                        onClick={() => nav(`/ai-drafts?t=library`)}
                        data-testid={`ai-recent-${d.id}`}
                        className="w-full flex items-center gap-3 p-2 rounded-md text-left hover:bg-slate-50 transition-colors"
                      >
                        <FileText size={14} className="text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{d.template_name}</div>
                          <div className="text-[10px] text-slate-500">{d.customer_name || "No customer"} · {d.language} · {(d.created_at || "").slice(0, 10)}</div>
                        </div>
                        <span className={`text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${
                          d.status === "Approved" ? "border-emerald-300 text-emerald-700 bg-emerald-50" :
                          d.status === "Archived" ? "border-slate-300 text-slate-600 bg-slate-50" :
                          "border-amber-300 text-amber-700 bg-amber-50"
                        }`}>{d.status || "Draft"}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                <button
                  onClick={() => nav("/ai-drafts?t=generate")}
                  data-testid="dashboard-ai-new"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200"
                >
                  <Sparkle size={16} weight="fill" /> Generate New Draft
                </button>
                <button
                  onClick={() => nav("/ai-drafts?t=library")}
                  data-testid="dashboard-ai-library"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200"
                >
                  <BookOpenText size={16} /> Open Draft Library
                </button>
              </div>
            </Card>
          </div>

          {/* Quick Find */}
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-3">Quick Find</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {QUICK_FIND.map((q) => {
                const toneBg = { orange: "hover:border-orange-400 hover:bg-orange-50", blue: "hover:border-blue-400 hover:bg-blue-50", emerald: "hover:border-emerald-400 hover:bg-emerald-50", purple: "hover:border-purple-400 hover:bg-purple-50", red: "hover:border-red-400 hover:bg-red-50", amber: "hover:border-amber-400 hover:bg-amber-50" }[q.accent];
                const iconTone = { orange: "text-orange-600", blue: "text-blue-700", emerald: "text-emerald-700", purple: "text-purple-700", red: "text-red-600", amber: "text-amber-700" }[q.accent];
                return (
                  <button
                    key={q.label}
                    onClick={() => nav(q.to)}
                    data-testid={`qf-${q.label.replace(/ /g, "-").toLowerCase()}`}
                    className={`flex flex-col items-start gap-2 p-4 rounded-lg bg-white border border-slate-200 transition-colors ${toneBg}`}
                  >
                    <q.icon size={22} weight="duotone" className={iconTone} />
                    <span className="text-sm font-medium text-slate-900">{q.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-6 bg-white border-slate-200" data-testid="chart-monthly-revenue">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Last 6 months</div>
                  <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Monthly Revenue</h3>
                </div>
              </div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={stats.monthly_revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff" }} formatter={(v) => inr(v)} />
                    <Bar dataKey="revenue" fill="#EA580C" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 bg-white border-slate-200" data-testid="chart-service-income">
              <div className="mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Top services</div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Service Income</h3>
              </div>
              {stats.service_income.length === 0 ? (
                <div className="text-sm text-slate-500 py-10 text-center">No paid applications yet.</div>
              ) : (
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={stats.service_income} dataKey="total" nameKey="service" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                        {stats.service_income.map((s, i) => <Cell key={s.service} fill={STAT_COLORS[i % STAT_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => inr(v)} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          {/* ============== BRANCH-WISE SUMMARY ============== */}
          {showBranchWise && branchRows.length > 0 && <BranchWiseSummary rows={branchRows} nav={nav} />}
        </>
      )}
    </div>
  );
}

// ==================================================================
// Branch-wise Summary (Super/Branch Admin)
// ==================================================================
function BranchWiseSummary({ rows, nav }) {
  const totals = rows.reduce((acc, r) => ({
    customers:  acc.customers  + r.customers_total,
    apps:       acc.apps       + r.applications_total,
    pending:    acc.pending    + r.pending_apps,
    revToday:   acc.revToday   + r.revenue_today,
    revMonth:   acc.revMonth   + r.revenue_month,
    out:        acc.out        + r.outstanding,
    complaints: acc.complaints + r.open_complaints,
  }), { customers: 0, apps: 0, pending: 0, revToday: 0, revMonth: 0, out: 0, complaints: 0 });

  const maxRev = Math.max(1, ...rows.map((r) => r.revenue_month));

  return (
    <div className="mt-8" data-testid="branch-wise-summary">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Buildings size={14} weight="fill" className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Branch-wise Breakdown</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Compare all branches</h2>
          <p className="text-sm text-slate-500 mt-0.5">Live counters for each branch — sorted by this month's revenue.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 font-semibold">Total revenue (mo): <span className="text-slate-900">{inr(totals.revMonth)}</span></span>
          <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 font-semibold">Outstanding: <span className="text-amber-900">{inr(totals.out)}</span></span>
        </div>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {rows.map((r) => {
          const pct = Math.round((r.revenue_month / maxRev) * 100);
          return (
            <Card
              key={r.branch_id}
              data-testid={`branch-row-${r.branch_id}`}
              onClick={() => nav(`/reports?branch_id=${r.branch_id}`)}
              className="p-5 bg-white border-slate-200 hover:border-orange-400 hover:shadow-md cursor-pointer transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                    <MapPin size={10} /> {r.code || "Branch"}
                  </div>
                  <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{r.name}</div>
                  <div className="text-[11px] text-slate-500">{r.city}{r.manager ? ` · Mgr ${r.manager}` : ""}</div>
                </div>
                <ArrowRight size={14} className="text-slate-300" />
              </div>

              {/* Revenue bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-slate-500 font-semibold uppercase tracking-wider">Revenue (this month)</span>
                  <span className="font-bold text-slate-900">{inr(r.revenue_month)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
                <MiniStat label="Customers" value={r.customers_total} today={r.customers_today} />
                <MiniStat label="Apps"      value={r.applications_total} today={r.applications_today} />
                <MiniStat label="Pending"   value={r.pending_apps}   tone={r.pending_apps > 10 ? "text-amber-700" : ""} />
                <MiniStat label="Staff"     value={r.staff_count} />
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                <div className="flex gap-3">
                  <span className="text-slate-600">Today: <span className="font-semibold text-slate-900">{inr(r.revenue_today)}</span></span>
                  {r.outstanding > 0 && <span className="text-amber-700 font-semibold">Out: {inr(r.outstanding)}</span>}
                </div>
                {r.open_complaints > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-semibold">
                    <Warning size={10} weight="fill" /> {r.open_complaints}
                  </span>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Compact comparison table */}
      <Card className="p-0 bg-white border-slate-200 overflow-hidden" data-testid="branch-wise-table">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Comparison Table</div>
          <span className="text-[10px] text-slate-400">Click any row to open branch reports</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Branch</th>
                <th className="text-right px-3 py-2.5 font-semibold">Customers</th>
                <th className="text-right px-3 py-2.5 font-semibold">Apps</th>
                <th className="text-right px-3 py-2.5 font-semibold">Pending</th>
                <th className="text-right px-3 py-2.5 font-semibold">Today ₹</th>
                <th className="text-right px-3 py-2.5 font-semibold">Month ₹</th>
                <th className="text-right px-3 py-2.5 font-semibold">GST ₹</th>
                <th className="text-right px-3 py-2.5 font-semibold">Outstanding</th>
                <th className="text-right px-3 py-2.5 font-semibold">Complaints</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.branch_id} onClick={() => nav(`/reports?branch_id=${r.branch_id}`)} className="hover:bg-orange-50/40 cursor-pointer" data-testid={`branch-tr-${r.branch_id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{r.name} <span className="text-slate-400 text-[10px]">· {r.code}</span></td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.customers_total.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.applications_total.toLocaleString("en-IN")}</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${r.pending_apps > 10 ? "text-amber-700 font-semibold" : ""}`}>{r.pending_apps.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{inr(r.revenue_today)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-900">{inr(r.revenue_month)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-600">{inr(r.gst_month)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${r.outstanding > 0 ? "text-amber-700 font-semibold" : ""}`}>{inr(r.outstanding)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono ${r.open_complaints > 0 ? "text-red-700 font-semibold" : ""}`}>{r.open_complaints}</td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="bg-slate-50 font-semibold" data-testid="branch-tr-total">
                <td className="px-4 py-2.5 text-slate-900">Total ({rows.length} branches)</td>
                <td className="px-3 py-2.5 text-right font-mono">{totals.customers.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5 text-right font-mono">{totals.apps.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5 text-right font-mono">{totals.pending.toLocaleString("en-IN")}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(totals.revToday)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-slate-900">{inr(totals.revMonth)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-slate-600">—</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(totals.out)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{totals.complaints}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function MiniStat({ label, value, today, tone = "" }) {
  return (
    <div>
      <div className={`font-bold ${tone || "text-slate-900"}`}>{(value || 0).toLocaleString("en-IN")}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold mt-0.5">{label}</div>
      {typeof today === "number" && today > 0 && (
        <div className="text-[9px] font-semibold text-emerald-600">+{today} today</div>
      )}
    </div>
  );
}
